import axios, { AxiosError } from 'axios';
import {
    QueryFunctionContext,
    useMutation,
    useQuery,
    useQueryClient,
} from 'react-query';
import {
    TeleobjectApiResponse,
    TeleobjectInhibitionRequest,
    TeleobjectInhibitionResult,
    TeleobjectInhibitionRow,
    TeleobjectPage,
    TeleobjectSearchRequest,
} from './Teleobjects.types';

export const TELEOBJECT_INHIBITION_QUERY_KEY = [
    'teleobjects',
    'inhibition',
] as const;

const apiUrl = 'api/teleobject/inhibition';

export const buildTeleobjectSearchParams = (
    request: TeleobjectSearchRequest,
): URLSearchParams => {
    const params = new URLSearchParams();
    params.set('page', request.pagination.page.toString());
    params.set('size', request.pagination.size.toString());

    Object.entries(request.filters).forEach(([field, value]) => {
        if (value === null || value === '') {
            return;
        }
        const condition = field === 'inhibited' ? 'equals' : 'contains';
        params.set(`${field}.${condition}`, String(value));
    });
    params.set(
        `${request.sort.field}.sort-by`,
        request.sort.direction,
    );
    return params;
};

export const fetchTeleobjectInhibition = async (
    request: TeleobjectSearchRequest,
    signal?: AbortSignal,
): Promise<TeleobjectPage<TeleobjectInhibitionRow>> => {
    const response = await axios.get<
        TeleobjectApiResponse<TeleobjectPage<TeleobjectInhibitionRow>>
    >(`${apiUrl}/search`, {
        params: buildTeleobjectSearchParams(request),
        signal,
        timeout: 30000,
    });
    return response.data.data;
};

export const useTeleobjectInhibitionSearch = (
    request: TeleobjectSearchRequest,
    refreshInterval: number,
) =>
    useQuery<
        TeleobjectPage<TeleobjectInhibitionRow>,
        AxiosError,
        TeleobjectPage<TeleobjectInhibitionRow>,
        readonly unknown[]
    >(
        [...TELEOBJECT_INHIBITION_QUERY_KEY, request],
        ({ signal }: QueryFunctionContext<readonly unknown[]>) =>
            fetchTeleobjectInhibition(request, signal),
        {
            keepPreviousData: false,
            refetchInterval: refreshInterval,
            refetchOnWindowFocus: true,
            retry: 1,
        },
    );

export const useLiveAccountAuthorities = (refreshInterval: number) =>
    useQuery<Array<{name?: string | null}>, AxiosError>(
        ['account', 'authorities', 'teleobjects'],
        async () => {
            const response = await axios.get('api/account');
            return response.data?.data?.authoritySet ?? [];
        },
        {
            refetchInterval: refreshInterval,
            refetchOnWindowFocus: true,
            retry: 1,
        },
    );

export const updateTeleobjectInhibition = async (
    request: TeleobjectInhibitionRequest,
): Promise<TeleobjectInhibitionResult> => {
    const response = await axios.post<
        TeleobjectApiResponse<TeleobjectInhibitionResult>
    >(apiUrl, request, { timeout: 30000 });
    return response.data.data;
};

export const useUpdateTeleobjectInhibition = () => {
    const queryClient = useQueryClient();

    return useMutation<
        TeleobjectInhibitionResult,
        AxiosError,
        TeleobjectInhibitionRequest
    >(updateTeleobjectInhibition, {
        onSuccess: async result => {
            const confirmedIds = new Set([
                ...result.updatedIds,
                ...result.unchangedIds,
            ]);
            queryClient.setQueriesData<
                TeleobjectPage<TeleobjectInhibitionRow> | undefined
            >(
                TELEOBJECT_INHIBITION_QUERY_KEY,
                page =>
                    page
                        ? {
                              ...page,
                              data: page.data.map(row =>
                                  confirmedIds.has(row.teleObjectId)
                                      ? {
                                            ...row,
                                            inhibited:
                                                result.finalInhibited,
                                        }
                                      : row,
                              ),
                          }
                        : page,
            );
            await queryClient.invalidateQueries(
                TELEOBJECT_INHIBITION_QUERY_KEY,
            );
        },
    });
};
